import { Icon, TokenIcon } from "@/components";
import {
  formatAbsolutePercent,
  formatAddressShort,
  formatTokenNumber,
  normalizeScaled,
  formatUsdMarketPrice,
  cn,
} from "@/utils";
import clsx from "clsx";

interface PriceChangeProps {
  priceChange24h: number;
  className?: string;
}

function PriceChange({ priceChange24h, className }: PriceChangeProps) {
  return (
    <p
      className={clsx(
        "font-bold flex items-center",
        {
          "text-positive": priceChange24h >= 0,
          "text-negative": priceChange24h < 0,
        },
        className,
      )}
    >
      <Icon
        name="arrow"
        className={clsx("w-2 h-2 mr-1", priceChange24h < 0 && "rotate-180")}
      />
      {formatAbsolutePercent(priceChange24h)}
    </p>
  );
}

interface TokenProps {
  address: string;
  name?: string;
  symbol?: string;
  logoURI?: string;
  price?: number | null;
  volume?: number;
  priceChange24h?: number | null;
  priceChange24hLeft?: boolean;
  quantityDetails?: { value: string; decimals: number };
  isSelected?: boolean;
  verified: boolean;
  showUnverified?: boolean;
  compact?: boolean;
  descriptionId?: string;
}

export const Token = ({
  address,
  name,
  symbol,
  logoURI,
  price,
  priceChange24h,
  priceChange24hLeft,
  quantityDetails,
  isSelected,
  verified,
  showUnverified,
  compact,
  descriptionId,
}: TokenProps) => {
  return (
    <div
      id={address}
      className={cn(
        "w-full text-sm sm:text-base flex gap-2.5 py-5 leading-none",
        {
          "py-3.5": compact,
        },
      )}
    >
      <div className="relative w-6 h-6">
        <TokenIcon
          // Pass a key to this component so that it doesn't display the previous
          // token icon while the new one loads in the virtual list
          key={address}
          logoURI={logoURI}
          alt=""
          className="min-w-6 w-6 h-6"
        />
        {showUnverified && !verified ? (
          <Icon
            name="warning"
            className="w-3 h-3 text-accent absolute -right-[1px] -bottom-[3px]"
          />
        ) : null}
      </div>
      <div className="flex-1 flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          {name ? (
            <p className="font-bold max-w-[150px] h-[15px] leading-none overflow-hidden text-ellipsis whitespace-nowrap text-sm">
              {name}
            </p>
          ) : null}
          {isSelected ? (
            <Icon name="circle-check" className="w-4 h-4 text-primary inline" />
          ) : (
            ""
          )}
        </div>

        {priceChange24hLeft && priceChange24h ? (
          <PriceChange priceChange24h={priceChange24h} />
        ) : (
          <div className="flex items-center gap-1.5" id={descriptionId}>
            <p className="text-gray-200 text-left text-sm">
              {symbol || formatAddressShort(address, 5)}
            </p>
            {symbol && (
              <p className="text-gray-200 text-left text-xxs bg-gray-700 px-1 flex items-center h-4 rounded-sm">
                {formatAddressShort(address, 5)}
              </p>
            )}
          </div>
        )}
      </div>
      <div className="text-right flex flex-col gap-1">
        <p className="font-bold text-sm">
          {price === undefined ? "N/A" : formatUsdMarketPrice(price)}
        </p>
        {priceChange24h && !priceChange24hLeft ? (
          <PriceChange
            priceChange24h={priceChange24h}
            className="justify-end"
          />
        ) : null}
        {quantityDetails ? (
          <span className="text-xs text-gray-200 text-right">
            {formatTokenNumber(
              normalizeScaled(quantityDetails.value, quantityDetails.decimals),
              quantityDetails.decimals,
              { decimalsMode: "fixed" },
            )}{" "}
            {symbol ?? formatAddressShort(address, 2)}
          </span>
        ) : null}
      </div>
    </div>
  );
};
